import {
  AuthTokens,
  AuthUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { CustomerRole, UserProfile, UserRole } from '.';

export const getUserProfile = async (
  user: AuthUser,
  tokens: AuthTokens,
): Promise<UserProfile | null> => {
  const attributes = await fetchUserAttributes();

  try {
    const username = user?.username;
    const email = attributes.email;
    const name =
      attributes?.name ?? email?.replace(/@.*/, '').replace('.', ' ');
    const groups = (tokens?.accessToken?.payload['cognito:groups'] ??
      []) as string[];

    const customers = groups.filter((g: string) => {
      return (
        g !== UserRole.ADMIN && g !== UserRole.ANALYST && g !== UserRole.VIEWER
      );
    });

    const role: UserRole = groups.reduce((acc: UserRole, group: string) => {
      if (group === UserRole.ADMIN) {
        return UserRole.ADMIN;
      }
      if (acc !== UserRole.ADMIN && group === UserRole.ANALYST) {
        return UserRole.ANALYST;
      }
      return acc;
    }, UserRole.VIEWER);

    // Customer role
    const customerRole =
      (attributes['custom:customer_role'] as CustomerRole) || CustomerRole.USER;

    return {
      username,
      email,
      name,
      groups,
      customers,
      role,
      customerRole,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return null;
  }
};

/**
 * This is a helper function to get the current Auth token. It is defined
 * here in the context so that all interactions with the Auth provider (in
 * this case Amplify) are kept in one place. It can easily be moved to
 * another place if needed.
 *
 * This function just fetches the correct token to use in API requests.
 * @returns The required Bearer token or undefined if no token is available.
 */
export async function getCurrentAuthToken(): Promise<string | undefined> {
  const { tokens } = await fetchAuthSession();

  const token = tokens?.idToken?.toString();
  return token;
}

/**
 * Reverses the normalization done by normalizeCompanyGroupName.
 * Converts a normalized company group name (e.g., "acme_corp") back to a readable company name.
 * @param customerId - The normalized company group name (e.g., "acme_corp" or "unknown")
 * @returns The readable company name (e.g., "Acme Corp" or "Unknown")
 */
export const denormalizeCompanyGroupName = (customerId: string): string => {
  let denormalized = customerId;

  // Handle the "unknown" case
  if (denormalized === 'unknown') {
    return 'Unknown';
  }

  // Replace underscores with spaces
  denormalized = denormalized.replace(/_/g, ' ');

  // Convert to title case (capitalize first letter of each word)
  denormalized = denormalized
    .split(' ')
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');

  return denormalized.trim() || 'Unknown';
};
